/**
 * Per-provider configuration for the shared single-matter import flow.
 *
 * The import *mechanics* are identical for every PMS — the backend routes are provider-parameterised
 * — so what a provider actually needs is a description of two things: how its matters can be looked
 * up, and which facts about a matter Vale cannot derive from it and must therefore ask a user for.
 * Everything the modal renders differently between Smokeball and ActionStep is one of those, declared
 * here rather than branched on inside the component.
 */

import type { PmsProvider } from '@/lib/services/integrations/pms';
import { getPmsProviderPresentationFor } from '@/lib/services/integrations/pms/provider-presentation';

/** One way of finding a matter in the PMS, and the copy that describes it truthfully. */
export interface PmsLookupMode {
  id: 'reference' | 'name';
  tabLabel: string;
  fieldLabel: string;
  placeholder: string;
  hint: string;
}

export interface PmsImportProviderConfig {
  provider: PmsProvider;
  /**
   * The PMS's brand name, as shown to the user.
   *
   * Sourced from the provider-presentation registry, not declared here — the label is shown on many
   * surfaces beyond this modal, and one owner is what keeps them agreeing (LF-6344).
   */
  label: string;
  /** Optional brand mark; providers with no asset render a neutral icon instead. */
  logoUrl?: string;
  /**
   * The lookup modes this provider's search genuinely supports, in tab order. A single mode renders
   * as a plain field with no tab bar.
   */
  lookupModes: readonly PmsLookupMode[];
  /**
   * The PMS's matter types cannot be mapped onto Vale's, so the user picks the Vale type.
   *
   * True for ActionStep: its catalogue is per-firm and has no probate type, leaving nothing to map
   * from. False for Smokeball, which derives the type from its own global matter-type table.
   */
  requiresValeMatterType: boolean;
  /**
   * The PMS carries no jurisdiction, so the user supplies the matter's state.
   *
   * Collected only for matter types that need one — see the modal's use of `isEstateAdminType`.
   */
  requiresState: boolean;
  /**
   * The PMS cannot identify a matter's decedent, so the user picks them from the matter's contacts.
   *
   * True for ActionStep, which has no native `Deceased` participant type. False for Smokeball, whose
   * matters carry a global `Deceased` role — there is nothing there for a user to decide.
   */
  requiresDecedentSelection: boolean;
  /**
   * Whether the user may **type** the decedent's name instead of picking one of the matter's contacts —
   * offered when the matter has no participant identifying the deceased, or the user chooses to type
   * rather than pick (`[ASTEP-23]`). Declared per provider, rather than the modal branching on the
   * provider's name, so a future provider that also needs `requiresDecedentSelection` can opt out if a
   * typed name would never make sense for it. Meaningless — and ignored — when
   * `requiresDecedentSelection` is false.
   */
  allowsTypedDecedentName: boolean;
}

const SMOKEBALL: PmsImportProviderConfig = {
  provider: 'smokeball',
  ...getPmsProviderPresentationFor('smokeball'),
  lookupModes: [
    {
      id: 'reference',
      tabLabel: 'Matter number',
      fieldLabel: 'Matter reference number',
      placeholder: 'Enter Smokeball matter reference...',
      hint: 'Enter the reference number of the Smokeball matter you want to import into Vale.'
    },
    {
      id: 'name',
      tabLabel: 'Client name',
      fieldLabel: 'Client name',
      placeholder: 'Enter the client’s name...',
      hint: 'Enter the name of the client on the Smokeball matter you want to import into Vale.'
    }
  ],
  requiresValeMatterType: false,
  requiresState: false,
  requiresDecedentSelection: false,
  allowsTypedDecedentName: false
};

/**
 * ActionStep offers **reference lookup only** — deliberately, not as a gap.
 *
 * Its matter search filters on the action's `reference` and otherwise falls back to substring-matching
 * the action *name*; it never searches client names. Offering a "Client name" tab would name a search
 * the PMS does not perform, so the one mode it does support is the one mode offered.
 */
const ACTIONSTEP: PmsImportProviderConfig = {
  provider: 'actionstep',
  ...getPmsProviderPresentationFor('actionstep'),
  lookupModes: [
    {
      id: 'reference',
      tabLabel: 'Matter reference',
      fieldLabel: 'Matter reference',
      placeholder: 'Enter Actionstep matter reference...',
      hint: 'Enter the reference of the Actionstep matter you want to import into Vale.'
    }
  ],
  requiresValeMatterType: true,
  requiresState: true,
  requiresDecedentSelection: true,
  allowsTypedDecedentName: true
};

const CONFIG_BY_PROVIDER: Record<PmsProvider, PmsImportProviderConfig> = {
  smokeball: SMOKEBALL,
  actionstep: ACTIONSTEP
};

export function getPmsImportProviderConfig(
  provider: PmsProvider
): PmsImportProviderConfig {
  return CONFIG_BY_PROVIDER[provider];
}
